import React, { useState, useEffect } from 'react';

import classNames from 'classnames';
import { Field } from 'react-final-form';

import { RangeSlider } from '../../../components';

import css from './IntegerRangeFilter.module.css';

const RADIX = 10;

const resolveMinMaxValues = (values, defaultMax, defaultMin) => {
  const { maxValue, minValue } = values || {};
  return {
    maxValue: maxValue != null ? maxValue : defaultMax,
    minValue: minValue != null ? minValue : defaultMin,
  };
};

const isTooSmall = (x, limit) => x < limit;
const isTooBig = (x, limit) => x > limit;
const isValidMin = (minValue, minLimit, maxValue) =>
  Number.isInteger(minValue) && !isTooSmall(minValue, minLimit) && !isTooBig(minValue, maxValue);
const isValidMax = (maxValue, maxLimit, minValue) =>
  Number.isInteger(maxValue) && !isTooBig(maxValue, maxLimit) && !isTooSmall(maxValue, minValue);

const getValidHandles = (currentValues, inputValues, minLimit, maxLimit) => {
  const isMinUpdated = currentValues.minValue !== inputValues.minValue;
  const isMaxUpdated = currentValues.maxValue !== inputValues.maxValue;

  if (isMinUpdated && isMaxUpdated) {
    const { minValue, maxValue } = inputValues;
    const inRange = !(isTooSmall(minValue, minLimit) || isTooBig(maxValue, maxLimit));
    return inRange && minValue <= maxValue ? [minValue, maxValue] : [minLimit, maxLimit];
  }

  const { minValue, maxValue } = inputValues;
  const validMin = !isMinUpdated
    ? currentValues.minValue
    : !Number.isInteger(minValue)
    ? minLimit
    : isTooSmall(minValue, minLimit)
    ? minLimit
    : isTooBig(minValue, maxValue)
    ? maxValue
    : minValue;
  const validMax = !isMaxUpdated
    ? currentValues.maxValue
    : !Number.isInteger(maxValue)
    ? maxLimit
    : isTooBig(maxValue, maxLimit)
    ? maxLimit
    : isTooSmall(maxValue, minValue)
    ? minValue
    : maxValue;

  return [validMin, validMax];
};

/**
 * RangeInput
 *
 * Renders the redesigned price/integer range input:
 * FROM / TO value boxes → dual-handle slider → boundary labels → preset buttons.
 *
 * @param {Function?} props.formatValue  optional (value: number) => string formatter (used by PriceFilter)
 * @param {Array?}    props.presets      optional [{label, min, max}] quick-pick buttons
 */
const RangeInput = props => {
  const {
    input,
    min: defaultMinValue,
    max: defaultMaxValue,
    step,
    isInSideBar,
    initialValues,
    intl,
    getLabelForRangeInput,
    formatValue,
    presets,
  } = props;
  const { value: values = {}, onChange, name } = input;

  const currentValues = resolveMinMaxValues(initialValues[name], defaultMaxValue, defaultMinValue);
  const [fieldValues, setFieldValues] = useState(currentValues);

  useEffect(() => {
    setFieldValues(currentValues);
  }, [initialValues]);

  const handleMinValueChange = event => {
    const newValue = Number.parseInt(event.target.value, RADIX);
    if (!Number.isInteger(newValue)) {
      setFieldValues({ ...fieldValues, minValue: '' });
      return;
    }
    if (newValue > fieldValues.maxValue || newValue < defaultMinValue) {
      setFieldValues({ ...fieldValues, minValue: newValue });
      return;
    }
    const clamped = newValue < defaultMinValue ? defaultMinValue : newValue;
    const newValues = { ...fieldValues, minValue: clamped };
    setFieldValues(newValues);
    onChange(newValues);
  };

  const handleMaxValueChange = event => {
    const newValue = Number.parseInt(event.target.value, RADIX);
    if (!Number.isInteger(newValue)) {
      setFieldValues({ ...fieldValues, maxValue: '' });
      return;
    }
    if (newValue < fieldValues.minValue || newValue > defaultMaxValue) {
      setFieldValues({ ...fieldValues, maxValue: newValue });
      return;
    }
    const clamped = newValue > defaultMaxValue ? defaultMaxValue : newValue;
    const newValues = { ...fieldValues, maxValue: clamped };
    setFieldValues(newValues);
    onChange(newValues);
  };

  const handleMinValueBlur = event => {
    const inputValue = Number.parseInt(event.target.value, RADIX);
    if (!Number.isInteger(inputValue) || inputValue < defaultMinValue || inputValue > values.maxValue) {
      const newValues = { ...fieldValues, minValue: defaultMinValue };
      setFieldValues(newValues);
      onChange(newValues);
    }
  };

  const handleMaxValueBlur = event => {
    const inputValue = Number.parseInt(event.target.value, RADIX);
    if (!Number.isInteger(inputValue) || inputValue < values.minValue || inputValue > defaultMaxValue) {
      const newValues = { ...fieldValues, maxValue: defaultMaxValue };
      setFieldValues(newValues);
      onChange(newValues);
    }
  };

  const handleSliderChange = updatedValue => {
    setFieldValues({ ...updatedValue });
    onChange({ ...updatedValue });
  };

  const handlePresetClick = preset => {
    const newValues = { minValue: preset.min, maxValue: preset.max };
    setFieldValues(newValues);
    onChange(newValues);
  };

  const labelForRangeInput = getLabelForRangeInput
    ? getLabelForRangeInput
    : (value, handleName) =>
        intl.formatMessage(
          { id: 'IntegerRangeFilter.screenreader.rangeHandle' },
          { value, handle: handleName }
        );

  const getHandleLabels = handles => {
    const multipleHandles = handles.length > 1;
    return handles.map((h, idx) => {
      const handleName =
        multipleHandles && idx === 0
          ? 'min'
          : multipleHandles && idx === handles.length - 1
          ? 'max'
          : 'handle';
      return labelForRangeInput(h, handleName);
    });
  };

  const isMinInvalid = !isValidMin(fieldValues.minValue, defaultMinValue, fieldValues.maxValue);
  const isMaxInvalid = !isValidMax(fieldValues.maxValue, defaultMaxValue, fieldValues.minValue);

  const validHandles = getValidHandles(values, fieldValues, defaultMinValue, defaultMaxValue);

  const fmt = v => (formatValue ? formatValue(v) : String(v));

  const isActivePreset = preset =>
    fieldValues.minValue === preset.min && fieldValues.maxValue === preset.max;

  // Sidebar redesigned layout
  if (isInSideBar) {
    return (
      <div className={css.formWrapper}>
        {/* ── FROM / TO value display ─────────────────────────────── */}
        <div className={css.valueRow}>
          <div className={css.valueBox}>
            <span className={css.valueBoxLabel}>
              {intl.formatMessage({ id: 'IntegerRangeFilter.fromLabel' })}
            </span>
            <div className={css.valueDisplayWrapper}>
              <span className={css.valueDisplayText}>{fmt(fieldValues.minValue)}</span>
              <input
                className={classNames(css.valueInput, { [css.invalidInput]: isMinInvalid })}
                type="number"
                inputMode="numeric"
                pattern="\d*"
                name={`${name}_min`}
                min={defaultMinValue}
                max={defaultMaxValue}
                placeholder={defaultMinValue}
                value={fieldValues.minValue}
                onChange={handleMinValueChange}
                onBlur={handleMinValueBlur}
                aria-invalid={isMinInvalid}
                aria-label={labelForRangeInput(fieldValues.minValue, 'min')}
              />
            </div>
          </div>

          <span className={css.emDash} aria-hidden="true">—</span>

          <div className={css.valueBox}>
            <span className={css.valueBoxLabel}>
              {intl.formatMessage({ id: 'IntegerRangeFilter.toLabel' })}
            </span>
            <div className={css.valueDisplayWrapper}>
              <span className={css.valueDisplayText}>{fmt(fieldValues.maxValue)}</span>
              <input
                className={classNames(css.valueInput, { [css.invalidInput]: isMaxInvalid })}
                type="number"
                inputMode="numeric"
                pattern="\d*"
                name={`${name}_max`}
                min={defaultMinValue}
                max={defaultMaxValue}
                placeholder={defaultMaxValue}
                value={fieldValues.maxValue}
                onChange={handleMaxValueChange}
                onBlur={handleMaxValueBlur}
                aria-invalid={isMaxInvalid}
                aria-label={labelForRangeInput(fieldValues.maxValue, 'max')}
              />
            </div>
          </div>
        </div>

        {/* ── Dual-handle slider ──────────────────────────────────── */}
        <div className={css.sliderWrapper}>
          <RangeSlider
            min={defaultMinValue}
            max={defaultMaxValue}
            ariaLabels={getHandleLabels(validHandles)}
            step={step}
            handles={validHandles}
            onChange={handles => handleSliderChange({ minValue: handles[0], maxValue: handles[1] })}
          />
        </div>

        {/* ── Boundary labels ─────────────────────────────────────── */}
        <div className={css.boundaryRow}>
          <span className={css.boundaryLabel}>{fmt(defaultMinValue)}</span>
          <span className={css.boundaryLabel}>{fmt(defaultMaxValue)}</span>
        </div>

        {/* ── Quick-pick presets ──────────────────────────────────── */}
        {presets && presets.length > 0 ? (
          <div className={css.presets}>
            {presets.map(preset => (
              <button
                key={preset.label}
                type="button"
                className={classNames(css.preset, { [css.presetActive]: isActivePreset(preset) })}
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  // Popup / non-sidebar layout (unchanged)
  return (
    <div>
      <div className={classNames(css.contentWrapper, css.contentWrapperSidebar)}>
        <div className={css.inputsWrapper}>
          <span className={css.labelPopup}>
            {intl.formatMessage({ id: 'IntegerRangeFilter.rangeInputsLabel' })}
          </span>
          <input
            className={classNames(css.minValue, { [css.invalidInput]: isMinInvalid })}
            inputMode="numeric"
            pattern="\d*"
            name={`${name}_min`}
            min={defaultMinValue}
            max={defaultMaxValue}
            placeholder={defaultMinValue}
            value={fieldValues.minValue}
            onChange={handleMinValueChange}
            onBlur={handleMinValueBlur}
            aria-invalid={isMinInvalid}
            aria-label={labelForRangeInput(fieldValues.minValue, 'min')}
          />
          <span className={css.valueSeparator}>-</span>
          <input
            className={classNames(css.maxValue, { [css.invalidInput]: isMaxInvalid })}
            inputMode="numeric"
            pattern="\d*"
            name={`${name}_max`}
            min={defaultMinValue}
            max={defaultMaxValue}
            placeholder={defaultMaxValue}
            value={fieldValues.maxValue}
            onChange={handleMaxValueChange}
            onBlur={handleMaxValueBlur}
            aria-invalid={isMaxInvalid}
            aria-label={labelForRangeInput(fieldValues.maxValue, 'max')}
          />
        </div>
      </div>
      <div className={css.sliderWrapper}>
        <RangeSlider
          min={defaultMinValue}
          max={defaultMaxValue}
          ariaLabels={getHandleLabels(validHandles)}
          step={step}
          handles={validHandles}
          onChange={handles => handleSliderChange({ minValue: handles[0], maxValue: handles[1] })}
        />
      </div>
    </div>
  );
};

/**
 * FieldSelectIntegerRange
 *
 * @component
 * @param {number}    props.min
 * @param {number}    props.max
 * @param {string}    props.name
 * @param {number}    [props.step]
 * @param {boolean}   [props.isInSideBar]
 * @param {Function}  [props.formatValue]  (value: number) => string
 * @param {Array}     [props.presets]      [{label, min, max}]
 */
const FieldSelectIntegerRange = props => {
  const { max, min, name, step = 5, isInSideBar = false, formatValue, presets, ...rest } = props;
  return (
    <Field
      max={max}
      min={min}
      name={name}
      step={step}
      isInSideBar={isInSideBar}
      formatValue={formatValue}
      presets={presets}
      component={RangeInput}
      {...rest}
    />
  );
};

export default FieldSelectIntegerRange;
